import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, forwardRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { COUNTRIES, CountryOption } from '../../data/countries';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { GeoService } from '../../../core/services/geo.service';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';

@Component({
    selector: 'app-country-selector',
    standalone: true,
    imports: [CommonModule, OverlayModule],
    templateUrl: './country-selector.component.html',
    styleUrls: ['./country-selector.component.scss'],
providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CountrySelectorComponent),
            multi: true
        }
    ]
})
export class CountrySelectorComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() disableAutoDetect = false;
    @Input() preferredCountry: string | null = null;
    // Filter mode: "" is a legitimate value meaning "every country". When this is
    // true the selector NEVER writes a value into the bound model on its own —
    // no `EG` default, no profile/IP detection — and the panel offers an explicit
    // "all countries" row. Forms that require a country leave this false.
    // Without it, a list filter bound to this selector silently narrows to Egypt
    // on load, hiding every unit outside it.
    @Input() allowEmpty = false;
    @Input() emptyLabel = 'All countries';

    private static readonly DEFAULT_COUNTRY_CODE = 'EG';

    value = '';
    countries: CountryOption[] = COUNTRIES;
    unknownValue: string | null = null;
    disabled = false;
    private hasUserValue = false;
    private destroyed$ = new Subject<void>();
    private autoAttempted = false;
    private readonly store = inject(Store);
    private readonly geoService = inject(GeoService);

    @ViewChild('originEl', { read: ElementRef }) private originEl?: ElementRef<HTMLElement>;
    @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

    panelOpen = false;
    searchQuery = '';
    originWidth = 0;
    readonly overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 }
    ];

    private onChangeFn: (value: string) => void = () => {};
    private onTouchedFn: () => void = () => {};

    ngOnInit(): void {
        this.tryAutoDetect();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    writeValue(obj: any): void {
        const raw = obj ? obj.toString().trim() : '';
        const normalized = this.normalizeCountry(raw);
        const displayValue = normalized || raw || '';
        const inList = this.countries.some(c => c.code === displayValue);
        this.value = displayValue;
        this.unknownValue = !inList && displayValue ? displayValue : null;
        // Treat non-default values as an explicit selection (ex: stored filters / edit forms).
        // Keep Egypt as the initial default so IP detection can still replace it.
        this.hasUserValue = Boolean(displayValue && displayValue !== CountrySelectorComponent.DEFAULT_COUNTRY_CODE);
        if (!this.autoAttempted) {
            this.tryAutoDetect();
        }
    }

    registerOnChange(fn: any): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedFn = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onChange(value: string) {
        this.value = value;
        this.unknownValue = this.countries.find(c => c.code === value) ? null : value;
        this.hasUserValue = true;
        this.onChangeFn(value);
    }

    onTouched() {
        this.onTouchedFn();
    }

    get selectedCountry(): CountryOption | null {
        if (!this.value) return null;
        return this.countries.find(c => c.code === this.value) || null;
    }

    get filteredCountries(): CountryOption[] {
        const query = String(this.searchQuery || '').trim().toLowerCase();
        if (!query) return this.countries;
        return this.countries.filter(country => {
            return country.name.toLowerCase().includes(query) || country.code.toLowerCase().includes(query);
        });
    }

    trackCountryCode(_: number, country: CountryOption): string {
        return country.code;
    }

    togglePanel(): void {
        if (this.disabled) return;
        if (this.panelOpen) {
            this.closePanel();
            return;
        }
        this.openPanel();
    }

    openPanel(): void {
        if (this.disabled) return;
        this.panelOpen = true;
        this.searchQuery = '';
        this.syncOriginWidth();
        setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    }

    closePanel(): void {
        if (!this.panelOpen) return;
        this.panelOpen = false;
        this.onTouched();
    }

    onSearch(value: string): void {
        this.searchQuery = String(value || '');
    }

    selectCountry(code: string): void {
        this.onChange(code);
        this.closePanel();
    }

    // Filter mode only: clear back to "all countries".
    clearSelection(): void {
        this.onChange('');
        this.closePanel();
    }

    triggerLabel(): string {
        if (!this.value) return this.allowEmpty ? this.emptyLabel : 'Select country';
        return this.selectedCountry?.name || this.unknownValue || this.value;
    }

    selectFirstFiltered(): void {
        const first = this.filteredCountries[0];
        if (!first) return;
        this.selectCountry(first.code);
    }

    private syncOriginWidth(): void {
        try {
            const rect = this.originEl?.nativeElement?.getBoundingClientRect?.();
            this.originWidth = rect?.width ? Math.ceil(rect.width) : 0;
        } catch {
            this.originWidth = 0;
        }
    }

    private async tryAutoDetect() {
        if (this.allowEmpty) {
            // Filter mode — an empty value means "all countries"; never auto-fill.
            this.autoAttempted = true;
            return;
        }
        if (this.autoAttempted || this.hasUserValue) return;
        this.autoAttempted = true;

        // Keep an already valid *non-default* value if set (ex: stored filters).
        // The default `EG` is only a placeholder until IP/profile detection finishes.
        const current = this.normalizeCountry(this.value);
        if (current && current !== CountrySelectorComponent.DEFAULT_COUNTRY_CODE) {
            if (this.setIfValid(current)) return;
        }

        // Parent hint
        if (this.preferredCountry && this.setIfValid(this.preferredCountry)) {
            // Still attempt IP auto-detection later if enabled; parent hint is a starting value only.
        }

        // Egypt default (until user-country is wired through auth/profile consistently).
        this.setIfValid(CountrySelectorComponent.DEFAULT_COUNTRY_CODE);

        if (this.disableAutoDetect) return;

        // Profile country (when logged in) takes precedence over IP lookup.
        try {
            const user = await firstValueFrom(this.store.select(selectCurrentUser).pipe(take(1)));
            const profileCountry = user?.country || (user as any)?.countryCode || (user as any)?.COUNTRY || null;
            if (this.setIfValid(profileCountry)) return;
        } catch {
            // ignore
        }

        // IP-based lookup (best effort, silent failure).
        const ipCountry = await this.lookupCountryFromIp();
        if (this.setIfValid(ipCountry)) return;
    }

    private setIfValid(candidate: string | null | undefined): boolean {
        const normalized = this.normalizeCountry(candidate);
        if (!normalized || this.hasUserValue) return false;
        this.value = normalized;
        this.unknownValue = this.countries.find(c => c.code === normalized) ? null : normalized;
        this.onChangeFn(normalized);
        return true;
    }

    private normalizeCountry(value: string | null | undefined): string | null {
        if (!value) return null;
        const trimmed = value.toString().trim();
        if (!trimmed) return null;
        const upper = trimmed.toUpperCase();
        const exact = this.countries.find(c => c.code === upper);
        if (exact) return exact.code;
        const byName = this.countries.find(c => c.name.toLowerCase() === trimmed.toLowerCase());
        if (byName?.code) return byName.code;
        return upper.length === 2 ? upper : trimmed;
    }

    private async lookupCountryFromIp(): Promise<string | null> {
        try {
            const { countryCode } = await this.geoService.getCountryFromIp();
            return this.normalizeCountry(countryCode);
        } catch {
            return null;
        }
    }
}
